/**
 * InsightsPage — iter83: "Insights for your hotel".
 * Route: /insights (auth required). Geo/market demand signals for hoteliers
 * built from free public-data APIs: Open-Meteo (weather), Nager.Date (source-
 * market holidays), Frankfurter/ECB (FX purchasing power), Ticketmaster
 * (local events, admin-configured key). Settings are per-user (location +
 * up to 8 guest source markets).
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  MapPin, CloudSun, CalendarDays, Ticket, TrendingUp, TrendingDown,
  Loader2, Settings2, ArrowRight, Minus, Newspaper, MessageCircleQuestion,
} from "lucide-react";
import { Link } from "react-router-dom";
import { API } from "../config";
import { useAuth } from "../AuthContext";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";

const CARD_SHADOW = { boxShadow: "0 0 0 1px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04)" };

function SectionCard({ icon: Icon, title, subtitle, children, testId }) {
  return (
    <section data-testid={testId} className="bg-white rounded-2xl p-6" style={CARD_SHADOW}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-[#006874]" />
        <h2 className="text-sm font-bold text-[#191c1d]">{title}</h2>
      </div>
      {subtitle && <p className="text-xs text-[#9aa7a9] mb-4">{subtitle}</p>}
      {children}
    </section>
  );
}

function SettingsForm({ supportedMarkets, initial, onSaved, onCancel }) {
  const [location, setLocation] = useState(initial?.location_query || "");
  const [markets, setMarkets] = useState(initial?.source_markets || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const toggleMarket = (code) => {
    setMarkets(m => m.includes(code) ? m.filter(x => x !== code) : (m.length < 8 ? [...m, code] : m));
  };

  const save = async (e) => {
    e?.preventDefault();
    if (location.trim().length < 3 || markets.length === 0 || saving) return;
    setSaving(true); setError(null);
    try {
      const r = await axios.put(`${API}/insights/settings`, { location: location.trim(), source_markets: markets });
      onSaved(r.data.settings);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail === "location_not_found"
        ? "We couldn't find that location — try adding the country, e.g. \"Rimini, Italy\"."
        : (typeof detail === "string" ? detail : "Could not save settings — please retry."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} data-testid="insights-settings-form" className="bg-white rounded-2xl p-6" style={CARD_SHADOW}>
      <h2 className="text-sm font-bold text-[#191c1d] mb-1">Where is your property or destination?</h2>
      <p className="text-xs text-[#9aa7a9] mb-3">Hotel location, city or destination — all signals are computed for this place.</p>
      <input
        data-testid="insights-location-input"
        value={location}
        onChange={e => setLocation(e.target.value)}
        placeholder='e.g. "Rimini, Italy" or "Lake Como"'
        maxLength={120}
        className="w-full text-sm text-[#191c1d] placeholder-[#9aa7a9] outline-none rounded-xl px-4 py-3"
        style={{ background: "#f5f7f8", border: "1px solid #e8ecee" }}
      />

      <h2 className="text-sm font-bold text-[#191c1d] mt-6 mb-1">Your guest source markets</h2>
      <p className="text-xs text-[#9aa7a9] mb-3">Pick up to 8 — holidays and purchasing-power signals track these countries.</p>
      <div className="flex flex-wrap gap-2">
        {supportedMarkets.map(m => {
          const on = markets.includes(m.code);
          return (
            <button key={m.code} type="button" data-testid={`insights-market-${m.code}`}
              onClick={() => toggleMarket(m.code)}
              className="text-xs px-3 py-1.5 rounded-full font-medium transition-colors"
              style={on ? { background: "#006874", color: "#fff" } : { background: "#f1f4f5", color: "#5f6b6d" }}>
              {m.name}
            </button>
          );
        })}
      </div>

      {error && <p data-testid="insights-settings-error" className="text-sm text-[#b45309] mt-4">{error}</p>}

      <div className="flex items-center gap-3 mt-6">
        <button type="submit" data-testid="insights-settings-save"
          disabled={saving || location.trim().length < 3 || markets.length === 0}
          className="flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold text-white transition-opacity disabled:opacity-40"
          style={{ background: "#006874" }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          {saving ? "Locating…" : "Show my insights"}
        </button>
        {onCancel && (
          <button type="button" data-testid="insights-settings-cancel" onClick={onCancel}
            className="text-sm text-[#6b7a7d] hover:text-[#191c1d]">Cancel</button>
        )}
      </div>
    </form>
  );
}

function WeatherCard({ weather }) {
  if (!weather) return null;
  return (
    <SectionCard icon={CloudSun} title="14-day weather outlook" subtitle="Open-Meteo forecast for your location" testId="insights-weather-card">
      <div className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: "thin" }}>
        {weather.days.map(d => (
          <div key={d.date} data-testid={`insights-weather-day-${d.date}`}
            className="flex-shrink-0 text-center rounded-xl px-2.5 py-2"
            style={{ background: d.weekday >= 5 ? "#f0f9fa" : "#f7f9f9", minWidth: 68 }}>
            <p className="text-[10px] font-semibold text-[#6b7a7d]">{d.date_label.slice(0, 6)}</p>
            <p className="text-sm font-bold text-[#191c1d] mt-1">{Math.round(d.tmax)}°</p>
            <p className="text-[10px] text-[#9aa7a9]">{Math.round(d.tmin)}°</p>
            <p className="text-[9px] text-[#6b7a7d] mt-1">{d.label}</p>
            <p className="text-[9px] text-[#9aa7a9]">{Math.round(d.precip_prob)}%</p>
          </div>
        ))}
      </div>
      {weather.signals?.length > 0 && (
        <ul className="mt-4 space-y-2">
          {weather.signals.map((s, i) => (
            <li key={i} data-testid={`insights-weather-signal-${i}`}
              className="text-sm rounded-xl px-3 py-2"
              style={s.type === "positive"
                ? { background: "#e7f6ec", color: "#166534" }
                : { background: "#fef6e0", color: "#92400e" }}>
              {s.text}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function CalendarCard({ calendar }) {
  if (!calendar) return null;
  return (
    <SectionCard icon={CalendarDays} title="Demand calendar" subtitle="Public holidays in your source markets, next 90 days" testId="insights-calendar-card">
      {calendar.items.length === 0 ? (
        <p className="text-sm text-[#6b7a7d]">No public holidays in your source markets over the next 90 days.</p>
      ) : (
        <ul className="space-y-2">
          {calendar.items.map((h, i) => (
            <li key={`${h.date}-${h.market}`} data-testid={`insights-holiday-${i}`}
              className="flex items-center gap-3 text-sm rounded-xl px-3 py-2" style={{ background: "#f7f9f9" }}>
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#006874]/10 text-[#006874] flex-shrink-0">{h.market}</span>
              <span className="text-xs text-[#6b7a7d] w-20 flex-shrink-0">{h.date_label}</span>
              <span className="text-[#191c1d] truncate">{h.name}</span>
              {h.long_weekend && (
                <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: "#e7f6ec", color: "#166534" }}>
                  Long weekend
                </span>
              )}
              {h.bridge && (
                <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: "#f0f9fa", color: "#006874" }}>
                  Bridge day
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function EventsCard({ events, isAdmin }) {
  return (
    <SectionCard icon={Ticket} title="Events near you" subtitle="Ticketmaster events within 30 km, next 60 days" testId="insights-events-card">
      {!events?.configured ? (
        <p data-testid="insights-events-unconfigured" className="text-sm text-[#6b7a7d]">
          {isAdmin
            ? "Add a Ticketmaster API key in Admin → API Keys to unlock local event signals."
            : "Local event signals are coming soon."}
        </p>
      ) : events.error ? (
        <p className="text-sm text-[#92400e]">Events are temporarily unavailable — check back shortly.</p>
      ) : events.events.length === 0 ? (
        <p className="text-sm text-[#6b7a7d]">No ticketed events found within 30 km in the next 60 days.</p>
      ) : (
        <ul className="space-y-2">
          {events.events.map((ev, i) => (
            <li key={`${ev.name}-${ev.date}`} data-testid={`insights-event-${i}`}
              className="flex items-center gap-3 text-sm rounded-xl px-3 py-2" style={{ background: "#f7f9f9" }}>
              <span className="text-xs text-[#6b7a7d] w-20 flex-shrink-0">{ev.date_label}</span>
              <span className="min-w-0">
                {ev.url ? (
                  <a href={ev.url} target="_blank" rel="noopener noreferrer" className="text-[#191c1d] font-medium hover:underline">{ev.name}</a>
                ) : <span className="text-[#191c1d] font-medium">{ev.name}</span>}
                <span className="text-[#9aa7a9]"> — {ev.venue}{ev.city ? `, ${ev.city}` : ""}</span>
              </span>
              {ev.segment && (
                <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#006874]/8 text-[#006874] flex-shrink-0">{ev.segment}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function DestinationNewsCard({ news }) {
  if (!news) return null;
  const destination = news.query_used || "your destination";
  const askHref = `/ask?q=${encodeURIComponent(`What's happening in ${destination} tourism right now?`)}`;
  return (
    <SectionCard icon={Newspaper} title="Your destination in the news"
      subtitle={`Recent TourismIntel coverage mentioning “${destination}”`}
      testId="insights-news-card">
      {news.items.length === 0 ? (
        <p data-testid="insights-news-empty" className="text-sm text-[#6b7a7d]">
          No recent coverage mentioning “{news.query_used}” — as soon as it appears in our sources, you&apos;ll see it here.
        </p>
      ) : (
        <ul className="space-y-2">
          {news.items.map((a, i) => (
            <li key={`${a.url}-${i}`} data-testid={`insights-news-${i}`}
              className="rounded-xl px-3 py-2" style={{ background: "#f7f9f9" }}>
              <div className="flex items-center gap-3 text-sm">
                <span className="text-xs text-[#6b7a7d] w-20 flex-shrink-0">{a.date_label}</span>
                <a href={a.url} target="_blank" rel="noopener noreferrer"
                  className="text-[#191c1d] font-medium hover:underline truncate">{a.title}</a>
                <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#006874]/8 text-[#006874] flex-shrink-0 truncate max-w-[140px]">{a.source}</span>
              </div>
              {a.summary && <p className="text-xs text-[#9aa7a9] mt-1 ml-[92px] line-clamp-2">{a.summary}</p>}
            </li>
          ))}
        </ul>
      )}
      <Link to={askHref} data-testid="insights-ask-destination"
        className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full text-xs font-semibold transition-colors"
        style={{ background: "#f0f9fa", color: "#006874" }}
        onMouseEnter={e => { e.currentTarget.style.background = "#006874"; e.currentTarget.style.color = "#fff"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#f0f9fa"; e.currentTarget.style.color = "#006874"; }}>
        <MessageCircleQuestion className="w-3.5 h-3.5" />
        Ask about {destination}
      </Link>
    </SectionCard>
  );
}

function FxCard({ fx }) {
  if (!fx) return null;
  const ICONS = { up: TrendingUp, down: TrendingDown, flat: Minus, same_currency: Minus };
  const COLORS = { up: "#166534", down: "#b91c1c", flat: "#6b7a7d", same_currency: "#9aa7a9" };
  return (
    <SectionCard icon={TrendingUp} title="Guest purchasing power"
      subtitle={`90-day currency trend vs ${fx.base_currency} (ECB reference rates)`} testId="insights-fx-card">
      <ul className="space-y-2">
        {fx.signals.map((s, i) => {
          const Icon = ICONS[s.direction] || Minus;
          return (
            <li key={s.market} data-testid={`insights-fx-${s.market}`}
              className="flex items-center gap-3 text-sm rounded-xl px-3 py-2" style={{ background: "#f7f9f9" }}>
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#006874]/10 text-[#006874] flex-shrink-0">{s.market}</span>
              <span className="text-[#191c1d]">{s.market_name}</span>
              <span className="ml-auto flex items-center gap-1.5 font-semibold" style={{ color: COLORS[s.direction] }}>
                <Icon className="w-3.5 h-3.5" />
                {s.direction === "same_currency" ? `same currency (${s.currency})` : `${s.strength_pct > 0 ? "+" : ""}${s.strength_pct}% ${s.currency}`}
              </span>
            </li>
          );
        })}
      </ul>
      {fx.signals.some(s => s.direction === "up") && (
        <p className="text-xs text-[#6b7a7d] mt-3">
          A strengthening guest currency means your rates look cheaper to that market — a tailwind for direct bookings.
        </p>
      )}
    </SectionCard>
  );
}

export default function InsightsPage() {
  const { isAdmin } = useAuth() || {};
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);       // /insights/overview payload
  const [supportedMarkets, setSupportedMarkets] = useState([]);
  const [editing, setEditing] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setLoadError(false);
    try {
      const r = await axios.get(`${API}/insights/overview`);
      setOverview(r.data);
      if (r.data.supported_markets) setSupportedMarkets(r.data.supported_markets);
      else {
        const s = await axios.get(`${API}/insights/settings`);
        setSupportedMarkets(s.data.supported_markets || []);
      }
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { document.title = "Demand Insights — TourismIntel"; load(); }, [load]);

  const configured = overview?.configured;
  const settings = overview?.settings;

  return (
    <div data-testid="insights-page" style={{ background: "#fafbfb", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <PublicPageNav active="insights" />

      <main className="flex-1 w-full" style={{ maxWidth: 1024, margin: "0 auto", padding: "40px 24px 64px" }}>
        <header className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-5 h-5 text-[#006874]" />
            <span className="text-xs font-bold tracking-widest uppercase text-[#006874]">Demand Insights</span>
            <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-full bg-[#006874]/10 text-[#006874]">BETA</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#191c1d]" style={{ letterSpacing: "-0.02em" }}>
            Demand signals for your property or destination.
          </h1>
          <p className="text-sm text-[#6b7a7d] mt-2 max-w-xl">
            Weather windows, source-market holidays, purchasing power, local events and news coverage — everything that moves bookings around your property or destination, in one view.
          </p>
        </header>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-[#6b7a7d]" data-testid="insights-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your insights…
          </div>
        ) : loadError ? (
          <div className="bg-white rounded-2xl p-6 text-sm text-[#92400e]" style={CARD_SHADOW} data-testid="insights-load-error">
            Something went wrong loading insights — please refresh.
          </div>
        ) : !configured || editing ? (
          <SettingsForm
            supportedMarkets={supportedMarkets}
            initial={editing ? settings : null}
            onSaved={() => { setEditing(false); load(); }}
            onCancel={editing ? () => setEditing(false) : null}
          />
        ) : (
          <>
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-[#6b7a7d]" data-testid="insights-location-summary">
                <span className="font-semibold text-[#191c1d]">{settings.geo?.display_name || settings.location_query}</span>
                {" · markets: "}{(settings.source_markets || []).join(", ")}
              </p>
              <button onClick={() => setEditing(true)} data-testid="insights-edit-settings"
                className="flex items-center gap-1.5 text-xs font-semibold text-[#006874] px-3 py-1.5 rounded-full transition-colors hover:bg-[#f0f9fa]">
                <Settings2 className="w-3.5 h-3.5" /> Edit
              </button>
            </div>
            <div className="space-y-5">
              <WeatherCard weather={overview.weather} />
              <div className="grid md:grid-cols-2 gap-5">
                <CalendarCard calendar={overview.demand_calendar} />
                <FxCard fx={overview.fx} />
              </div>
              <DestinationNewsCard news={overview.destination_news} />
              <EventsCard events={overview.events} isAdmin={isAdmin} />
              {!overview.weather && !overview.demand_calendar && !overview.fx && (
                <div className="bg-white rounded-2xl p-6 text-sm text-[#92400e]" style={CARD_SHADOW} data-testid="insights-all-failed">
                  Signal providers are temporarily unreachable — try again in a few minutes.
                </div>
              )}
            </div>
          </>
        )}
      </main>

      <PublicPageFooter />
    </div>
  );
}
